<template>
  <div id="app">
    <div class="background-animation"></div>
    <header>
    <div class="header-content">
      <DynamicLogo :isPaused="isBackgroundStatic" />
      <button @click="toggleBackgroundAnimation" class="bg-toggle" :title="isBackgroundStatic ? 'Enable animation' : 'Disable animation'">
        {{ isBackgroundStatic ? '▶' : '⏸' }}
      </button>
                <div class="language-switcher">
                  <button
                    v-for="supportedLang in supportedLanguages"
                    :key="supportedLang"
                    @click="switchLanguage(supportedLang)"
                    :class="{ 'active-lang': lang === supportedLang }"
                    class="lang-button"
                  >
                    {{ supportedLang.toUpperCase() }}
                  </button>
                </div>
              </div>
          </header>
    <router-view></router-view>


  </div>
</template>

<script>
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@vueuse/head'; // Import useHead
import { useLanguage } from './composables/useLanguage'; // Import useLanguage
import DynamicLogo from '@/components/DynamicLogo.vue';

export default {
  name: 'App',
  components: {
    DynamicLogo
  },
  setup() {
    const isBackgroundStatic = ref(false);
    const route = useRoute();
    const router = useRouter();
    const { lang, supportedLanguages } = useLanguage();

    // Set the lang attribute on the html tag reactively
    useHead({
      htmlAttrs: {
        lang: lang,
      }
    });

    const toggleBackgroundAnimation = () => {
      isBackgroundStatic.value = !isBackgroundStatic.value;
      document.querySelector('.background-animation').style.animationPlayState = 
        isBackgroundStatic.value ? 'paused' : 'running';
    };

    const switchLanguage = (newLang) => {
      const currentPath = route.fullPath;
      
      // If we are on an article page, we need to redirect to the equivalent article in the new language.
      // However, we don't have the logic to find the equivalent slug here.
      // So, the simplest SEO-safe approach is to redirect to the homepage of the new language.
      // If the user is on a non-article page, they can remain on that page but with the new language prefix.
      if (currentPath.includes('/article/')) {
        router.push(`/${newLang}`); // Redirect to new language homepage
      } else {
        const pathSegments = currentPath.split('/');
        // If the current path already has a language prefix, replace it
        if (supportedLanguages.includes(pathSegments[1])) {
          pathSegments[1] = newLang;
          router.push(pathSegments.join('/'));
        } else {
          // If not, add the new language prefix. This might happen if someone manually
          // navigates to a non-language prefixed route (e.g., /admin) which should not be affected,
          // but for user-facing routes, it should already be prefixed.
          // As a fallback, we'll push to the home page with the new language.
          router.push(`/${newLang}`);
        }
      }
    };

    return {
      isBackgroundStatic,
      toggleBackgroundAnimation,
      switchLanguage,
      lang,
      supportedLanguages
    };
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap');

body, html {
  margin: 0;
  padding: 0;
  height: 100%;
  font-family: 'Poppins', sans-serif;
}

.home {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  color: #ffffff;
  position: relative;
  overflow: hidden;
}

.background-animation {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(-45deg, #ee7752, #e73c7e, #23a6d5, #23d5ab);
  background-size: 400% 400%;
  animation: gradient 15s ease infinite;
  z-index: -1;
}

@keyframes gradient {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

header {
  text-align: center;
  padding: 20px 0;
}

.header-content {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.bg-toggle {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
  padding: 10px;
  border-radius: 50%;
  transition: background-color 0.3s ease;
}

.bg-toggle:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.language-switcher {
  display: flex;
  gap: 5px;
  margin-left: 15px;
}

.lang-button {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: #ffffff;
  font-size: 0.9rem;
  padding: 5px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease, border-color 0.3s ease;
}

.lang-button:hover {
  background-color: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.6);
}

.lang-button.active-lang {
  background-color: #e91e63;
  border-color: #e91e63;
  font-weight: 600;
}

.logo {
  display: inline-block;
  background: rgba(255, 255, 255, 0.1);
  padding: 10px 20px;
  border-radius: 10px;
}

.logo-text {
  font-size: 32px;
  font-weight: 700;
}

.logo-number {
  color: #2ecc71;
}

main {
  flex-grow: 1;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
}

.search-container {
  margin-bottom: 20px;
}

.search-input {
  width: 100%;
  padding: 15px 20px;
  font-size: 18px;
  border: none;
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  transition: all 0.3s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.search-input:focus {
  outline: none;
  background: rgba(255, 255, 255, 0.3);
}

.suggestion-cards {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
}

.suggestion-card {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 20px;
  padding: 10px 20px;
  color: #ffffff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.suggestion-card:hover {
  background: rgba(255, 255, 255, 0.3);
}

.articles-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.article-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 20px;
  transition: all 0.3s ease;
  position: relative;
  backdrop-filter: blur(10px);
}

.article-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.article-category {
  position: absolute;
  top: 15px;
  right: 15px;
  background: #2ecc71;
  color: #ffffff;
  padding: 5px 10px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: bold;
}

.article-card h3 {
  font-size: 22px;
  margin-bottom: 10px;
}

.article-card p {
  font-size: 16px;
  margin-bottom: 15px;
  color: rgba(255, 255, 255, 0.8);
}

.read-more {
  color: #2ecc71;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;
}

.read-more:hover {
  color: #ffffff;
}

footer {
  text-align: center;
  padding: 20px 0;
  font-size: 14px;
  background: rgba(0, 0, 0, 0.2);
}
</style>